import api from '@/services/api';
import type {
  ProcedureType,
  CreateProcedureTypeRequest,
  UpdateProcedureTypeRequest,
} from '@/types';

const BASE_PATH = '/procedure-types';

/**
 * Procedure type management API service.
 *
 * Handles CRUD and activation/deactivation for procedure types.
 */
export const procedureTypeService = {
  /**
   * Lists all procedure types.
   */
  async getProcedureTypes(): Promise<ProcedureType[]> {
    const { data } = await api.get<{ items: ProcedureType[] }>(BASE_PATH);
    return data.items;
  },

  /**
   * Fetches a single procedure type by ID.
   */
  async getProcedureType(id: string): Promise<ProcedureType> {
    const { data } = await api.get<ProcedureType>(`${BASE_PATH}/${id}`);
    return data;
  },

  /**
   * Creates a new procedure type.
   */
  async createProcedureType(payload: CreateProcedureTypeRequest): Promise<ProcedureType> {
    const { data } = await api.post<ProcedureType>(BASE_PATH, payload);
    return data;
  },

  /**
   * Updates an existing procedure type.
   */
  async updateProcedureType(
    id: string,
    payload: UpdateProcedureTypeRequest,
  ): Promise<ProcedureType> {
    const { data } = await api.put<ProcedureType>(`${BASE_PATH}/${id}`, payload);
    return data;
  },

  /**
   * Activates a procedure type.
   */
  async activateProcedureType(id: string): Promise<ProcedureType> {
    const { data } = await api.put<ProcedureType>(`${BASE_PATH}/${id}/activate`);
    return data;
  },

  /**
   * Deactivates a procedure type.
   */
  async deactivateProcedureType(id: string): Promise<ProcedureType> {
    const { data } = await api.put<ProcedureType>(`${BASE_PATH}/${id}/deactivate`);
    return data;
  },
};
